import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faFileCircleCheck, faShieldHalved } from '@fortawesome/free-solid-svg-icons'

import { MEDICAL_DOCUMENTS } from '../data/medicalSupplies'
import type { FaqItem } from '../lib/seo'
import { MedicalWhatsAppLink } from './MedicalProductCatalog'

export const MEDICAL_FAQ: FaqItem[] = [
  {
    q: 'Berapa minimum order perlengkapan medis di Dropify?',
    a: 'Sarung tangan medis mulai 10 box; minimum pemesanan masker dikonfirmasi admin sesuai tipe. Kirim nama produk, ukuran atau warna, dan jumlah per pilihan. Admin akan mengecek apakah permintaan campur produk dapat dipenuhi dari stok yang tersedia.',
  },
  {
    q: 'Masker apa saja yang tersedia di kategori perlengkapan medis?',
    a: 'Masker COMET Theo dalam empat tipe: 3 ply earloop isi 50 pcs, 4D isi 20 pcs, KN95 isi 20 pcs, dan 4 ply carbon isi 50 pcs. Perbandingan lapisan, warna, data BFE, dan dokumen per tipe tersedia di halaman masker COMET.',
  },
  {
    q: 'Apakah harga distributor ditampilkan di website?',
    a: 'Tidak. Harga mengikuti produk, ukuran, jumlah box, dan tujuan pengiriman. Kirim rincian tersebut melalui WhatsApp agar admin dapat memeriksa stok dan menyiapkan quotation.',
  },
  {
    q: 'Apakah semua produk merupakan sarung tangan steril untuk pembedahan?',
    a: 'Daftar yang tersedia saat ini berisi sarung tangan pemeriksaan dan sarung tangan sekali pakai. Status steril serta penggunaan bedah harus tertulis pada label dan dokumen SKU. Minta konfirmasi sebelum PO jika fasilitas membutuhkan sarung tangan bedah steril.',
  },
  {
    q: 'Dokumen apa yang dapat diminta sebelum pengadaan?',
    a: 'Dokumen dapat mencakup izin edar AKD Kemenkes, data TKDN, sertifikat halal, food grade, CE atau MDR, dan dokumen produsen. Admin akan memeriksa jenis dokumen dan masa berlakunya untuk SKU yang dipilih.',
  },
  {
    q: 'Apa arti FDA 510(k) pada sarung tangan medis?',
    a: 'FDA 510(k) clearance diberikan pada perangkat tertentu setelah dinilai substantially equivalent. Clearance satu model tidak otomatis berlaku pada seluruh produk produsen. Nomor dokumen harus dicocokkan dengan nama perangkat dan kemasan.',
  },
  {
    q: 'Apa yang perlu diperhatikan saat memilih sarung tangan latex?',
    a: 'Pastikan fasilitas memeriksa riwayat sensitivitas terhadap latex dan mengikuti SOP keselamatan internal. Pilih ukuran yang sesuai dan konfirmasikan penggunaan sarung tangan berbedak jika fasilitas menerapkan pembatasan.',
  },
  {
    q: 'Bagaimana cara meminta quotation?',
    a: 'Kirim nama produk atau bahan, ukuran, pembagian jumlah per ukuran, total box, kota tujuan, target penerimaan, serta daftar dokumen wajib. Admin akan memeriksa stok dan menyiapkan quotation.',
  },
]

export function MedicalDocumentation() {
  return (
    <section className="relative overflow-hidden bg-[#102d3d] py-16 text-white md:py-20">
      <div aria-hidden className="absolute -right-28 top-10 h-80 w-80 rounded-full border-[56px] border-sky-300/10" />
      <div className="relative mx-auto max-w-7xl px-4">
        <div className="grid gap-8 lg:grid-cols-[0.7fr_1.3fr] lg:items-end">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-sky-300">
              <FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4" />
              Dokumen pengadaan
            </p>
            <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Periksa cakupan dokumen sampai ke nama SKU</h2>
          </div>
          <p className="max-w-3xl text-base leading-relaxed text-slate-200">
            Sarung tangan Shamrock diproduksi PT Haloni Jane Tbk, yang mencantumkan sistem mutu dan dokumen produk untuk pasar Indonesia, Amerika Serikat, serta Eropa. Masker COMET Theo memakai standar CE dan ISO 13485, dengan nomor AKD pada tipe tertentu. Salinan sertifikat, nomor registrasi, masa berlaku, dan kecocokan kemasan diperiksa per SKU sebelum PO.
          </p>
        </div>

        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {MEDICAL_DOCUMENTS.map((document) => (
            <article key={document.name} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
              <FontAwesomeIcon icon={faFileCircleCheck} className="h-6 w-6 text-sky-300" />
              <h3 className="mt-4 text-lg font-black tracking-tight">{document.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{document.scope}</p>
            </article>
          ))}
        </div>

        <MedicalWhatsAppLink
          label="Minta quotation dan dokumen SKU"
          placement="documentation"
          className="mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-green-700 px-6 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
        />
      </div>
    </section>
  )
}

export function MedicalFaq() {
  return (
    <section className="bg-white py-16 md:py-20">
      <div className="mx-auto max-w-4xl px-4">
        <div className="text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-800">Pertanyaan pengadaan</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">Jawaban sebelum memilih produk dan mengirim PO</h2>
        </div>
        <div className="mt-9 divide-y divide-slate-200 border-y border-slate-200">
          {MEDICAL_FAQ.map((item) => (
            <details key={item.q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-slate-950">
                {item.q}
                <FontAwesomeIcon icon={faChevronDown} className="h-5 w-5 flex-shrink-0 text-sky-800 transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}
